/**
 * Atlas PM · 任务详情 · 删除任务二次确认弹窗（P7.6）。
 *
 * ⚠️ 后端为**软删除**（置 deletedAt）：指标 / 要素数据仍在库中，但随任务一并不可见。
 *    本弹窗文案据实表达「从项目中移除 / 不可见 / 无法恢复」，不承诺「彻底删除」。
 *
 * 删除鉴权在后端 TaskService.remove() 完成（超管 / 创建人 / 负责人）；
 * 越权返回 403，message 原样展示在弹窗内（不吞错）。
 */

import { useEffect } from 'react';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { ApiError } from '../../api/client';
import type { TaskDetail } from '@atlaspm/shared';
import { useDeleteTask } from './useTaskDetail';

export interface DeleteTaskDialogProps {
  task: TaskDetail | null;
  open: boolean;
  onClose(): void;
  onDeleted?(id: string): void;
}

export default function DeleteTaskDialog({
  task,
  open,
  onClose,
  onDeleted,
}: DeleteTaskDialogProps) {
  const remove = useDeleteTask();

  // 每次打开清掉上一次的失败信息，避免跨任务串错
  useEffect(() => {
    if (open) remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = () => {
    if (!task) return;
    remove.mutate(task.id, {
      onSuccess: () => {
        onDeleted?.(task.id);
        onClose();
      },
    });
  };

  const errorMessage =
    remove.error instanceof ApiError
      ? remove.error.message
      : remove.error instanceof Error
        ? remove.error.message
        : null;

  return (
    <Modal
      open={open}
      size="sm"
      danger
      title={`删除任务 · ${task?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            取消
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            disabled={remove.isPending}
            data-testid="task-delete-confirm"
          >
            {remove.isPending ? '删除中…' : '确认删除'}
          </Button>
        </>
      }
    >
      <div className="pm-delete-meta">
        任务编号：<b>{task?.code ?? '—'}</b>
      </div>

      <div className="pm-delete-explain">
        确认删除任务「<b>{task?.name ?? ''}</b>」？删除后该任务将从项目中移除，
        其指标与要素数据一并不可见，且<b>无法恢复</b>。
      </div>

      {errorMessage && (
        <div className="pm-modal__err" data-testid="task-delete-error">
          ⚠ {errorMessage}
        </div>
      )}
    </Modal>
  );
}
